// src/pages/ModeSelect.jsx
//
// Стартовый экран (показывается ДО логина).
// Выбор: "Я магазин" / "Я покупатель" / "Войти как сотрудник" / "Гость".
// На iOS кнопка "Я магазин" скрыта (требование Apple — никаких сторонних регистраций
// бизнес-аккаунтов мимо App Store биллинга).

import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import Icon from '../components/Icon';
import LanguageSelect from '../components/LanguageSelect';
import { canRegisterShop } from '../utils/platform';

export default function ModeSelect({ onPick }) {
  const { t } = useTranslation();
  const { enterAsGuest } = useAuth();

  const pickShop     = () => onPick('shop');
  const pickCustomer = () => onPick('customer');
  const pickEmployee = () => onPick('employee');
  const pickGuest    = () => enterAsGuest();

  const showShop = canRegisterShop();

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      padding: 24,
      background: 'var(--bg)',
    }}>
      {/* Селектор языка вверху справа */}
      <div style={{
        position: 'absolute',
        top: 'calc(env(safe-area-inset-top) + 12px)',
        right: 16,
        width: 160,
      }}>
        <LanguageSelect compact />
      </div>

      {/* Логотип + название */}
      <div style={{ marginTop: 'calc(env(safe-area-inset-top) + 48px)', textAlign: 'center' }}>
        <div style={{
          width: 72, height: 72, margin: '0 auto 16px',
          background: 'linear-gradient(135deg, var(--brand-primary), var(--brand-accent))',
          borderRadius: 20,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 8px 24px rgba(99, 102, 241, 0.25)',
          color: '#fff',
        }}>
          <Icon name="store" size={36} stroke={2} />
        </div>
        <h1 style={{
          fontSize: 32, fontWeight: 800,
          letterSpacing: '-0.03em',
          marginBottom: 8,
        }}>
          Alsatuu
        </h1>
        <p style={{
          fontSize: 14,
          color: 'var(--fg-muted)',
          lineHeight: 1.5,
        }}>
          {t('roleSelect.subtitle')}
        </p>
      </div>

      {/* Основные карточки */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        margin: '32px 0 auto',
      }}>
        {showShop && (
          <RoleCard
            iconName="store"
            title={t('roleSelect.shop')}
            desc={t('roleSelect.shopDesc')}
            onClick={pickShop}
          />
        )}
        <RoleCard
          iconName="star"
          title={t('roleSelect.customer')}
          desc={t('roleSelect.customerDesc')}
          onClick={pickCustomer}
        />
      </div>

      {/* Вторичные действия — сотрудник и гость */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        marginBottom: 'calc(env(safe-area-inset-bottom) + 16px)',
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          color: 'var(--fg-faint)',
          fontSize: 12,
          marginBottom: 8,
        }}>
          <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
          <span>{t('auth.or')}</span>
          <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
        </div>

        <button
          onClick={pickEmployee}
          style={{
            background: 'none', border: 'none',
            color: 'var(--fg-muted)',
            fontSize: 14, fontWeight: 600,
            padding: 10, cursor: 'pointer',
            fontFamily: 'var(--font-display)',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
          }}
        >
          <Icon name="users" size={14} />
          {t('auth.asEmployee')}
        </button>

        <button
          onClick={pickGuest}
          style={{
            background: 'none', border: 'none',
            color: 'var(--brand-primary)',
            fontSize: 14, fontWeight: 600,
            padding: 10, cursor: 'pointer',
            fontFamily: 'var(--font-display)',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
          }}
        >
          <Icon name="user" size={14} />
          {t('auth.asGuest')}
        </button>
      </div>
    </div>
  );
}

function RoleCard({ iconName, title, desc, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        padding: 18,
        background: 'var(--bg-elevated)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--r-md)',
        cursor: 'pointer',
        fontFamily: 'inherit',
        textAlign: 'left',
        color: 'var(--fg)',
        transition: 'transform 120ms var(--ease-spring)',
        width: '100%',
      }}
      onMouseDown={(e) => e.currentTarget.style.transform = 'scale(0.98)'}
      onMouseUp={(e) => e.currentTarget.style.transform = ''}
      onMouseLeave={(e) => e.currentTarget.style.transform = ''}
    >
      <div style={{
        width: 52, height: 52, borderRadius: 'var(--r-sm)',
        background: 'var(--brand-primary-soft)', color: 'var(--brand-primary)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
      }}>
        <Icon name={iconName} size={26} stroke={2} />
      </div>
      <div style={{ flex: 1 }}>
        <p style={{ fontSize: 17, fontWeight: 700, fontFamily: 'var(--font-display)' }}>{title}</p>
        <p style={{ fontSize: 13, color: 'var(--fg-muted)', marginTop: 2 }}>{desc}</p>
      </div>
      <Icon name="chevronRight" size={20} stroke={2.5} color="var(--fg-faint)" />
    </button>
  );
}
